'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';
import isBrowser from '../../../features/helpers/is-browser';
import useCurrentBreakpoint from '../../../features/hooks/use-current-breakpoint';
import { useHasMounted } from '../../../features/hooks/use-has-mounted';
const useResponsive = ({ children, direction = 'down', inactive, breakpoint, }) => {
    const hasMounted = useHasMounted();
    const query = useMemo(() => {
        if (direction === 'down') {
            return { maxWidth: breakpoint };
        }
        return { minWidth: breakpoint + 1 };
    }, [direction, breakpoint]);
    const isResponsive = useMediaQuery(query);
    if (!hasMounted)
        return children;
    if (inactive || isResponsive)
        return children;
    return null;
};
export const UltraWideDesktop = (props) => {
    const theme = useTheme();
    return useResponsive(Object.assign(Object.assign({}, props), { breakpoint: theme.breakpoints.ultra }));
};
export const WideDesktop = (props) => {
    const theme = useTheme();
    return useResponsive(Object.assign(Object.assign({}, props), { breakpoint: theme.breakpoints.wide }));
};
export const ExtraLargeDesktop = (props) => {
    const theme = useTheme();
    return useResponsive(Object.assign(Object.assign({}, props), { breakpoint: theme.breakpoints.xxl }));
};
export const LargeDesktop = (props) => {
    const theme = useTheme();
    return useResponsive(Object.assign(Object.assign({}, props), { breakpoint: theme.breakpoints.xl }));
};
export const MediumDesktop = (props) => {
    const theme = useTheme();
    return useResponsive(Object.assign(Object.assign({}, props), { breakpoint: theme.breakpoints.l }));
};
export const SmallDesktop = (props) => {
    const theme = useTheme();
    return useResponsive(Object.assign(Object.assign({}, props), { breakpoint: theme.breakpoints.md }));
};
export const Tablet = (props) => {
    const theme = useTheme();
    return useResponsive(Object.assign(Object.assign({}, props), { breakpoint: theme.breakpoints.tablet }));
};
export const Mobile = (props) => {
    const theme = useTheme();
    return useResponsive(Object.assign(Object.assign({}, props), { breakpoint: theme.breakpoints.mobile }));
};
export const useMediaStyle = ({ initial, tablet, xl, l, }) => {
    const theme = useTheme();
    const isXL = useMediaQuery({ maxWidth: theme.breakpoints.xxl });
    const isL = useMediaQuery({ maxWidth: theme.breakpoints.l });
    const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.tablet });
    return useMemo(() => {
        const currentStyle = {};
        const addStyling = (property, value) => {
            if (value !== undefined) {
                currentStyle[property] = value;
            }
        };
        const prepareStyling = (style) => {
            Object.entries(style).forEach(([key, value]) => {
                addStyling(key, value);
            });
        };
        prepareStyling(initial);
        if (isXL && !!xl)
            prepareStyling(xl);
        if (isL && !!l)
            prepareStyling(l);
        if (isTablet && !!tablet)
            prepareStyling(tablet);
        return currentStyle;
    }, [isXL, isL, isTablet, initial, xl, l, tablet]);
};
export const ResponsiveContent = ({ ultra: Ultra, wide: Wide, desktop: Desktop, tablet: Tablet, mobile: Mobile, smallDesktop: SmallDesktop, mediumDesktop: MediumDesktop, largeDesktop: LargeDesktop, xxlDesktop: XXLDesktop, props, }) => {
    const breakpoint = useCurrentBreakpoint();
    if (breakpoint === 'ultra' && Ultra)
        return _jsx(Ultra, Object.assign({}, props));
    if (breakpoint === 'wide' && Wide)
        return _jsx(Wide, Object.assign({}, props));
    if (breakpoint === 'xxl' && XXLDesktop)
        return _jsx(XXLDesktop, Object.assign({}, props));
    if (breakpoint === 'xl' && LargeDesktop)
        return _jsx(LargeDesktop, Object.assign({}, props));
    if (breakpoint === 'l' && MediumDesktop)
        return _jsx(MediumDesktop, Object.assign({}, props));
    if (breakpoint === 'md' && SmallDesktop)
        return _jsx(SmallDesktop, Object.assign({}, props));
    if (breakpoint === 'tablet' && Tablet)
        return _jsx(Tablet, Object.assign({}, props));
    if (breakpoint === 'mobile' && Mobile)
        return _jsx(Mobile, Object.assign({}, props));
    return _jsx(Desktop, Object.assign({}, props));
};
export const useMediaStyling = (props) => {
    const breakpoint = useCurrentBreakpoint();
    if (!isBrowser)
        return props.default;
    if (breakpoint === 'ultra' && !!props.ultra)
        return Object.assign(Object.assign({}, props.default), props.ultra);
    if (breakpoint === 'wide' && !!props.wide)
        return Object.assign(Object.assign({}, props.default), props.wide);
    if (breakpoint === 'xxl' && !!props.xxl)
        return Object.assign(Object.assign({}, props.default), props.xxl);
    if (breakpoint === 'xl' && !!props.xl)
        return Object.assign(Object.assign({}, props.default), props.xl);
    if (breakpoint === 'l' && !!props.l)
        return Object.assign(Object.assign({}, props.default), props.l);
    if (breakpoint === 'md' && !!props.md)
        return Object.assign(Object.assign({}, props.default), props.md);
    if (breakpoint === 'tablet' && !!props.tablet)
        return Object.assign(Object.assign({}, props.default), props.tablet);
    if (breakpoint === 'mobile' && !!props.mobile)
        return Object.assign(Object.assign({}, props.default), props.mobile);
    return props.default;
};
